interface IdItem {
  id: string;
}

export interface ReorderRequest {
  before_id: string | null;
  after_id: string | null;
}

export function neighborsForReorder<T extends IdItem>(
  reordered: T[],
  movedId: string,
): ReorderRequest {
  const newIndex = reordered.findIndex((t) => t.id === movedId);
  if (newIndex === -1) return { before_id: null, after_id: null };
  const prev = reordered[newIndex - 1];
  const next = reordered[newIndex + 1];
  return {
    before_id: prev?.id ?? null,
    after_id: next?.id ?? null,
  };
}

export type ReorderTarget =
  | { kind: "root" }
  | { kind: "subtask"; parentId: string }
  | { kind: "noop" };

// Decides what a drag means from the dragged row's and the drop target's
// parentId. Reorder is only ever allowed among siblings of the same parent;
// any cross-level drag onto another row is a no-op (snap back). The one drag that
// does change depth is a drop on a bucket — see promotedSubtask.
export function classifyReorder(
  activeParentId: string | null,
  overParentId: string | null,
): ReorderTarget {
  if (activeParentId !== overParentId) return { kind: "noop" };
  if (activeParentId === null) return { kind: "root" };
  return { kind: "subtask", parentId: activeParentId };
}

interface DragData {
  kind?: string;
  taskId?: string;
  parentId?: string | null;
}

/**
 * The subtask a drop on a bucket in the sidebar promotes to a top-level task, or null when the
 * drag means something else. Sidebar owns the mutation (it owns the drop targets); the
 * pages use this to spot the same gesture and drop the row from the subtree cache they
 * keep apart from the local copy.
 */
export function promotedSubtask(
  active: DragData | undefined,
  over: { kind?: string } | undefined,
): { taskId: string; parentId: string } | null {
  if (active?.kind !== "task" || !active.taskId) return null;
  if (over?.kind !== "list") return null;
  const parentId = active.parentId ?? null;
  return parentId === null ? null : { taskId: active.taskId, parentId };
}
